import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ExternalLink, PenLine } from "lucide-react";
import { FORMULATOR_REDRAFT_INSTRUCTION_MAX } from "@ttc-spectree/toolbox-contract";
import { AdoContent } from "@/components/ado/ado-content";
import { AdoPatCallout } from "@/components/ado/ado-pat-callout";
import { DraftEditor } from "@/components/formulator/draft-editor";
import { DraftSourceLabel, FieldDiffList } from "@/components/formulator/field-diff-view";
import { FormulatorNotice } from "@/components/formulator/formulator-notice";
import { MissingItemsList } from "@/components/formulator/missing-items-list";
import { Chip } from "@/components/grooming/grooming-chips";
import { useCan } from "@/hooks/queries/use-toolbox-access";
import {
  useSaveWorkItemInterviewDraft,
  useSubmitWorkItemInterview,
  useWorkItemInterviewReview,
} from "@/hooks/queries/use-work-item-interview";
import {
  getInterviewErrorCode,
  type FieldDiff,
  type InterviewKind,
  type InterviewReview,
  type InterviewSubmitResult,
} from "@/lib/api/work-item-interview";
import { getAdoPatErrorCode, ITEM_DETAIL_STATE, workItemPagePath } from "@/lib/api/work-items";
import { showGroomingToast } from "@/lib/grooming/toasts";
import { draftFromEditValues, editValuesFromDraft, editValuesFromReview, type DraftEditValues } from "@/lib/formulator/draft-text";
import { splitReproSteps } from "@/lib/formulator/repro-steps";
import { plainBridgeError } from "@/lib/toolbox/bridge-error";
import {
  canRedraft,
  formulatorErrorMessage,
  formulatorRedraftMessage,
  useFormulatorBridge,
  type FormulatorErrorMessage,
  type FormulatorTurnState,
} from "@/lib/toolbox/formulator";
import { FORMULATOR_PATH } from "@/lib/toolbox/tools";
import { cn } from "@/lib/utils";
import { TOOLBOX_PAGE_TITLE } from "@/lib/toolbox/type-scale";

/** Shown when submit is refused because the item changed in ADO since the interview started. */
export const WORK_ITEM_CHANGED_MESSAGE = "This item changed in ADO since you started. Reload and review again.";

/** Shown in place of the text while Claude writes it from the complete answers (epic PI-SPEC-SW-E321). */
export const CLAUDE_WRITING_TEXT = "Claude is writing the text";

/** Shown above the text put together from the answers when Claude's draft turn failed. */
export const DRAFT_FAILED_NOTE =
  "Claude could not write the text this time, so it is put together from your answers. You can still edit it or create the item.";

/** Shown when an edit was refused because the answers changed since the text was shown. */
export const DRAFT_STALE_MESSAGE =
  "Your answers changed since this text was shown, so nothing was saved. The text has been reloaded; check it, then edit again.";

/** The Redraft activity line when the engine has not sent one yet. */
export const REDRAFT_ACTIVITY = "Claude is rewriting the text";

/**
 * The Toolbox Formulator's review and create view (feature PI-SPEC-SW-1418;
 * design Write view `poReview`): the item as it will appear in ADO (fields,
 * area and iteration), whether it meets the grooming bar, the parts of the
 * project it touches, and Create in ADO (or Update in ADO when improving).
 *
 * The write goes straight to the SpecTree API with the review's `diffHash`,
 * so what is written is exactly what the person reviewed (D7). Improving an
 * existing item shows each changed field before and after; the original text
 * stays in the item's ADO history.
 *
 * Claude drafts (epic PI-SPEC-SW-E321, feature PI-SPEC-SW-1721): once the
 * interview is complete, Claude writes the final text from the answers. The
 * review says who wrote what it shows (Claude, the person's edit, or no label
 * for text put together from the answers), offers Edit text (saved through
 * `PUT /draft`) and, when the Toolbox offers `formulator.redraft`, a Redraft
 * box. Both refresh the review, so Create in ADO always sends the `diffHash`
 * of the text on screen. An older Toolbox has no `redraft`: no Redraft box,
 * and Edit text still works.
 */

/** Field labels as the design names them, per kind. Fields not listed keep the API's label. */
const FIELD_LABELS: Record<InterviewKind, Partial<Record<FieldDiff["field"], string>>> = {
  story: { description: "Story", acceptanceCriteria: "Done when", systemInfo: "System info", severity: "Severity" },
  bug: { reproSteps: "Repro steps", description: "Details", systemInfo: "System info", severity: "Severity" },
};

/** The order the rows read in: the body first, then the rest. */
const FIELD_ORDER: Record<InterviewKind, FieldDiff["field"][]> = {
  story: ["description", "acceptanceCriteria", "reproSteps", "systemInfo", "severity"],
  bug: ["reproSteps", "description", "acceptanceCriteria", "systemInfo", "severity"],
};

function eyebrow(review: InterviewReview): string {
  if (review.mode === "improve") return `Improve #${review.itemId ?? ""}`;
  return review.kind === "bug" ? "New bug" : "New user story";
}

function errorText(error: unknown, fallback: string): string {
  return plainBridgeError(error, fallback);
}

/**
 * "What happens next" (UX-PM-10). Names the Project Manager only when the
 * author is not one: `authorManagesAgenda` is the author's `manage-agenda` on
 * the item's project (Project Manager, Admin), false while access loads.
 * It no longer says the item appears in My Items: a new item is not assigned
 * to its author (01-roles.md R22). Pure; exported for tests.
 */
export function nextStepText(mode: "create" | "improve", container: string, authorManagesAgenda: boolean): string {
  if (mode === "improve") {
    return "The new text replaces the item's current text in ADO, as you. The original text stays in the item's history.";
  }
  const lands = `It lands in the ${container} backlog as Ready to groom.`;
  return authorManagesAgenda
    ? `${lands} Set its priority in ADO and add it to a grooming session's agenda when it is ready.`
    : `${lands} The Project Manager sets its priority and brings it to a grooming session.`;
}

/** `Area · iteration`, without the separator when the item has no iteration. Pure; exported for tests. */
export function destinationLine(destination: InterviewReview["destination"]): string {
  return [destination.area, destination.iteration].filter((part) => part.trim() !== "").join(" · ");
}

function FieldRow({ label, children, testId }: { label: string; children: ReactNode; testId?: string }) {
  return (
    <div
      className="grid grid-cols-[150px_minmax(0,1fr)] gap-4 border-b border-[#1A1A1E] py-[11px]"
      data-testid={testId ?? "formulator-review-field"}
    >
      <span className="font-medium text-muted-foreground">{label}</span>
      <div className="min-w-0 leading-[1.55]">{children}</div>
    </div>
  );
}

const EMPTY_VALUE = <span className="italic text-muted-foreground">Empty</span>;

/** One field's new value: tracker HTML through AdoContent (sanitised, ADO's lists and headings), plain text as text. */
function FieldValue({ diff }: { diff: FieldDiff }) {
  if (diff.format === "html") {
    return <AdoContent html={diff.after} tone="dark" empty={EMPTY_VALUE} />;
  }
  return diff.after.trim() === "" ? EMPTY_VALUE : <span className="whitespace-pre-wrap break-words">{diff.after}</span>;
}

/**
 * A bug's Repro steps as ADO will show them (UX-PM-44): the steps as a
 * numbered list in the Repro steps row, then Expected, Actual and Frequency
 * each in a row of its own. Labelled values are plain React text.
 */
function ReproStepsRows({ diff, label }: { diff: FieldDiff; label: string }) {
  const split = useMemo(() => splitReproSteps(diff.after), [diff.after]);
  return (
    <>
      <FieldRow label={label} testId="formulator-review-field">
        <AdoContent html={`${split.stepsHtml}${split.rest}`} tone="dark" testId="formulator-repro-steps" empty={EMPTY_VALUE} />
      </FieldRow>
      {split.labelled.map((row, index) => (
        <FieldRow key={`${row.label}-${String(index)}`} label={row.label}>
          <span className="whitespace-pre-wrap break-words" data-testid="formulator-repro-labelled">
            {row.text === "" ? EMPTY_VALUE : row.text}
          </span>
        </FieldRow>
      ))}
    </>
  );
}

function PartsTouched({ review }: { review: InterviewReview }) {
  return (
    <div className="glass-card flex flex-col gap-2 rounded-[10px] px-[18px] py-4" data-testid="formulator-parts">
      <h2 className="font-display text-[13px] font-semibold text-[#E4E4E7]">Parts of {review.container} it touches</h2>
      {review.productAreas.length === 0 ? (
        <span className="text-[13px] text-muted-foreground">Claude did not name any part of the product.</span>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {review.productAreas.map((touch) => (
            <Chip key={touch.area} tone={touch.level === "check" ? "none" : "cur"} testId="formulator-part">
              {touch.level === "check" ? `${touch.area} (check only)` : touch.area}
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
}

function Done({
  result,
  container,
  authorManagesAgenda,
}: {
  result: InterviewSubmitResult;
  container: string;
  authorManagesAgenda: boolean;
}) {
  const created = result.mode === "create";
  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="flex w-full max-w-[560px] flex-col gap-4" data-testid="formulator-done">
        <div className="flex items-center gap-2 text-green-400">
          <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
          <h1 className={cn("text-foreground", TOOLBOX_PAGE_TITLE)}>
            {created ? `Created #${result.ref.id} in ADO` : `Updated #${result.ref.id} in ADO`}
          </h1>
        </div>
        <p className="leading-relaxed text-muted-foreground" data-testid="formulator-done-next">
          {created
            ? nextStepText("create", container, authorManagesAgenda)
            : "The new text is in ADO now. The original text stays in the item's history."}
        </p>
        <div className="flex flex-wrap items-center gap-4 text-[13px]">
          <Link to={workItemPagePath(result.ref)} state={ITEM_DETAIL_STATE} className="font-medium text-link hover:underline" data-testid="formulator-done-item">
            Open #{result.ref.id} in SpecTree
          </Link>
          <a
            href={result.ref.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-link hover:underline"
          >
            Open in Azure DevOps
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
          <Link to="/ado/items" className="text-link hover:underline">
            My Items
          </Link>
          <span className="flex-1" />
          <Link
            to={FORMULATOR_PATH}
            className="inline-flex h-[30px] items-center rounded-lg border border-[#27272A] bg-[#1A1A1E] px-[11px] font-medium hover:bg-[#222227]"
          >
            Write another
          </Link>
        </div>
      </div>
    </div>
  );
}

export interface FormulatorReviewProps {
  sessionId: string;
  /**
   * The session's current turn from `useFormulatorEvents`, owned by the page
   * so a draft turn that started during the interview is still seen here.
   * Idle when omitted.
   */
  turn?: FormulatorTurnState;
  /** Back to the interview without writing anything. */
  onKeepEditing: () => void;
  /** Submit was refused: the draft is below the grooming bar; back to the interview. */
  onBelowBar: () => void;
  /** The item changed in ADO since the interview started: improve it again from its current text. */
  onRestartImprove: (itemId: string) => void;
}

const IDLE_TURN: FormulatorTurnState = { running: false, activity: null, lastFinished: null };

/**
 * The Redraft box (epic PI-SPEC-SW-E321): what to change, in the person's
 * words, sent through `formulator.redraft`. The turn's progress shows below;
 * the review refreshes when the turn finishes.
 */
function RedraftBox({
  instruction,
  onInstructionChange,
  running,
  activity,
  failure,
  disabled,
  onRedraft,
}: {
  instruction: string;
  onInstructionChange: (text: string) => void;
  running: boolean;
  activity: string | null;
  failure: FormulatorErrorMessage | null;
  disabled: boolean;
  onRedraft: () => void;
}) {
  return (
    <div className="glass-card flex flex-col gap-2 rounded-[10px] px-[18px] py-4" data-testid="formulator-redraft">
      <h2 className="font-display text-[13px] font-semibold text-[#E4E4E7]">Ask Claude to change it</h2>
      <textarea
        value={instruction}
        onChange={(event) => {
          onInstructionChange(event.target.value);
        }}
        maxLength={FORMULATOR_REDRAFT_INSTRUCTION_MAX}
        rows={3}
        disabled={running}
        aria-label="What should Claude change"
        placeholder="For example: make the steps shorter, or say it is for site managers."
        className="w-full resize-y rounded-[10px] border border-[#3F3F46] bg-[#111113] px-3 py-2 text-[13px] leading-[1.5] text-foreground outline-none focus:border-[#66A1FF] focus:ring-2 focus:ring-[#66A1FF]/25 disabled:opacity-50"
        data-testid="formulator-redraft-instruction"
      />
      {running ? (
        <div className="flex items-center gap-2.5 text-[13px] text-muted-foreground" role="status" data-testid="formulator-redraft-progress">
          <span
            className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-primary border-r-transparent"
            aria-hidden="true"
          />
          {activity ?? REDRAFT_ACTIVITY}
        </div>
      ) : (
        <div className="flex">
          <span className="flex-1" />
          <button
            type="button"
            onClick={onRedraft}
            disabled={disabled || instruction.trim() === ""}
            className="inline-flex h-[30px] items-center rounded-lg border border-[#27272A] bg-[#1A1A1E] px-[11px] text-[13px] font-medium hover:bg-[#222227] disabled:pointer-events-none disabled:opacity-40"
            data-testid="formulator-redraft-button"
          >
            Redraft
          </button>
        </div>
      )}
      {failure && <FormulatorNotice message={failure} testId="formulator-redraft-error" />}
    </div>
  );
}

export function FormulatorReview({
  sessionId,
  turn = IDLE_TURN,
  onKeepEditing,
  onBelowBar,
  onRestartImprove,
}: FormulatorReviewProps) {
  const reviewQuery = useWorkItemInterviewReview(sessionId);
  const submit = useSubmitWorkItemInterview();
  const saveDraft = useSaveWorkItemInterviewDraft();
  const formulator = useFormulatorBridge();
  const [result, setResult] = useState<InterviewSubmitResult | null>(null);
  // The editor's opening values and the basis the review showed when it opened.
  // The basis is kept until Save, so a review refetched mid-edit (a turn that
  // changed an answer) cannot stamp old text with a newer basis: the API then
  // refuses the save as DRAFT_STALE instead of storing it.
  const [editing, setEditing] = useState<{ values: DraftEditValues; basis: string } | null>(null);
  const [staleEdit, setStaleEdit] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [redraftSent, setRedraftSent] = useState(false);
  const [redraftFailure, setRedraftFailure] = useState<FormulatorErrorMessage | null>(null);
  const seenFinished = useRef(turn.lastFinished);
  const submitCode = getInterviewErrorCode(submit.error);
  const patError = getAdoPatErrorCode(submit.error) ?? getAdoPatErrorCode(reviewQuery.error);
  const review = reviewQuery.data;
  const { allowed: authorManagesAgenda } = useCan(review?.container, "manage-agenda");

  // A turn that finished after a Redraft was sent ends it: a failure keeps the
  // instruction and says why; success clears the box (the review refreshes itself).
  useEffect(() => {
    const finished = turn.lastFinished;
    if (finished === seenFinished.current) return;
    seenFinished.current = finished;
    if (!redraftSent || !finished) return;
    setRedraftSent(false);
    if (finished.outcome === "failed") {
      setRedraftFailure(formulatorRedraftMessage(formulatorErrorMessage(finished.errorCode, finished.message)));
    } else {
      setInstruction("");
    }
  }, [redraftSent, turn.lastFinished]);

  if (result && review) {
    return <Done result={result} container={review.container} authorManagesAgenda={authorManagesAgenda} />;
  }

  if (patError) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="w-full max-w-[640px]">
          <AdoPatCallout code={patError} />
        </div>
      </div>
    );
  }

  if (reviewQuery.isPending) {
    return (
      <div className="flex flex-1 items-center justify-center p-8" role="status">
        <span className="text-[13px] text-muted-foreground">Putting the draft together</span>
      </div>
    );
  }

  if (reviewQuery.isError || !review) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <FormulatorNotice
          className="w-full max-w-[640px]"
          message={{ text: errorText(reviewQuery.error, "The draft could not be loaded."), setup: false }}
          testId="formulator-review-error"
        >
          <button
            type="button"
            className="font-medium text-link hover:underline"
            onClick={() => {
              void reviewQuery.refetch();
            }}
          >
            Try again
          </button>
          <button type="button" className="text-muted-foreground hover:text-foreground" onClick={onKeepEditing}>
            Keep editing
          </button>
        </FormulatorNotice>
      </div>
    );
  }

  const isCreate = review.mode === "create";
  const { readyForGrooming, grooming } = review.projectedReadiness;
  const titleDiff = review.fields.find((diff) => diff.field === "title");
  const answeredTitle = titleDiff?.after.trim() ?? "";
  const title = answeredTitle !== "" ? answeredTitle : isCreate ? "Untitled" : `Item #${review.itemId ?? ""}`;
  const order = FIELD_ORDER[review.kind];
  const bodyFields = review.fields
    .filter((diff) => diff.field !== "title")
    .sort((a, b) => order.indexOf(a.field) - order.indexOf(b.field));
  const destination = destinationLine(review.destination);
  const nothingToWrite = review.fields.length === 0;
  const draftCurrent = review.draftStatus === "current";
  const draftBasis = review.draftBasis ?? null;
  // Complete with no draft yet while a turn runs: Claude is writing the text.
  const claudeWriting = review.complete && !draftCurrent && turn.running && !redraftSent;
  const draftFailed =
    review.complete && !draftCurrent && !turn.running && turn.lastFinished?.outcome === "failed" && !redraftSent;
  const redraftRunning = redraftSent || (draftCurrent && turn.running);
  // An improve without a draft shows the item's own text with the answers added, which is not an editable draft.
  const canEdit = draftBasis !== null && (draftCurrent || isCreate) && !claudeWriting && !redraftRunning;
  const showRedraft = canRedraft(formulator) && draftCurrent && editing === null;
  const busy = submit.isPending || reviewQuery.isFetching || saveDraft.isPending || claudeWriting || redraftRunning || editing !== null;

  const startEditing = () => {
    setStaleEdit(false);
    saveDraft.reset();
    if (draftBasis === null) return;
    setEditing({
      values: review.draft && draftCurrent ? editValuesFromDraft(review.draft) : editValuesFromReview(review),
      basis: draftBasis,
    });
  };

  const save = (values: DraftEditValues) => {
    if (editing === null) return;
    setStaleEdit(false);
    saveDraft.mutate(
      { sessionId, draft: draftFromEditValues(values, editing.basis) },
      {
        onSuccess: () => {
          setEditing(null);
        },
        onError: (error) => {
          if (getInterviewErrorCode(error) === "DRAFT_STALE") {
            setStaleEdit(true);
            setEditing(null);
            void reviewQuery.refetch();
          }
        },
      }
    );
  };

  const redraft = () => {
    const text = instruction.trim();
    if (!canRedraft(formulator) || text === "") return;
    setRedraftFailure(null);
    setRedraftSent(true);
    void formulator
      .redraft(sessionId, text)
      .then((outcome) => {
        if (!outcome.ok) {
          setRedraftSent(false);
          setRedraftFailure(formulatorRedraftMessage(formulatorErrorMessage(outcome.errorCode, outcome.message)));
        }
      })
      .catch(() => {
        setRedraftSent(false);
        setRedraftFailure(formulatorErrorMessage("UNKNOWN"));
      });
  };

  const body = isCreate ? (
    bodyFields.map((diff) => {
      const label = FIELD_LABELS[review.kind][diff.field] ?? diff.label;
      return review.kind === "bug" && diff.field === "reproSteps" && diff.format === "html" ? (
        <ReproStepsRows key={diff.field} diff={diff} label={label} />
      ) : (
        <FieldRow key={diff.field} label={label}>
          <FieldValue diff={diff} />
        </FieldRow>
      );
    })
  ) : nothingToWrite ? (
    <p className="py-3 text-muted-foreground" data-testid="formulator-no-changes">
      Nothing would change yet. Keep editing and answer the remaining questions.
    </p>
  ) : (
    <div className="py-3">
      <FieldDiffList review={review} />
    </div>
  );

  const write = () => {
    submit.mutate(
      { sessionId, diffHash: review.diffHash },
      {
        onSuccess: (response) => {
          setResult(response.data);
          showGroomingToast(
            response.data.mode === "create"
              ? `Created in ADO. It lands in the ${review.container} backlog as Ready to groom.`
              : `Updated #${response.data.ref.id} in ADO.`
          );
        },
        onError: (error) => {
          const code = getInterviewErrorCode(error);
          // The draft moved under the person (another window answered a question): reload it so they
          // create exactly what they see.
          if (code === "DIFF_NOT_APPROVED") void reviewQuery.refetch();
          if (code === "BELOW_GROOMING_BAR") onBelowBar();
        },
      }
    );
  };

  return (
    <div className="flex min-h-0 flex-1 gap-6 px-7 pb-5 pt-1.5" data-testid="formulator-review">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
        <div className="flex items-center gap-2">
          <span className="flex-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground" data-testid="formulator-review-eyebrow">
            {eyebrow(review)}
          </span>
          {readyForGrooming ? (
            <Chip tone="ok" testId="formulator-meets-standard">
              Meets the standard
            </Chip>
          ) : (
            <Chip tone="warn" testId="formulator-below-standard">
              Below the standard
            </Chip>
          )}
        </div>
        <h1 className={cn("mb-2 mt-1.5", TOOLBOX_PAGE_TITLE)} data-testid="formulator-review-title">
          {title}
        </h1>

        {(draftCurrent || canEdit) && editing === null && !claudeWriting && (
          <div className="mb-1 flex items-center gap-2">
            <DraftSourceLabel review={review} className="flex-1" />
            {!draftCurrent && <span className="flex-1" />}
            {canEdit && (
              <button
                type="button"
                onClick={startEditing}
                disabled={submit.isPending || reviewQuery.isFetching}
                className="inline-flex h-[26px] items-center gap-1.5 rounded-lg border border-[#27272A] bg-[#1A1A1E] px-[9px] text-[12px] font-medium hover:bg-[#222227] disabled:opacity-40"
                data-testid="formulator-edit-text"
              >
                <PenLine className="h-3.5 w-3.5" aria-hidden="true" />
                Edit text
              </button>
            )}
          </div>
        )}

        {staleEdit && editing === null && (
          <FormulatorNotice className="mb-2" message={{ text: DRAFT_STALE_MESSAGE, setup: false }} testId="formulator-draft-stale" />
        )}
        {draftFailed && editing === null && (
          <FormulatorNotice className="mb-2" message={{ text: DRAFT_FAILED_NOTE, setup: false }} testId="formulator-draft-failed" />
        )}

        {!readyForGrooming && grooming.missing.length > 0 && (
          <div className="mb-2 rounded-lg border border-amber-500/20 bg-amber-500/[0.05] p-3">
            <MissingItemsList items={grooming.missing} testId="formulator-review-missing" />
          </div>
        )}

        {claudeWriting ? (
          <div className="flex items-center gap-2.5 py-6 text-[13px] text-muted-foreground" role="status" data-testid="formulator-claude-writing">
            <span
              className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-primary border-r-transparent"
              aria-hidden="true"
            />
            <span>
              {CLAUDE_WRITING_TEXT}
              {turn.activity ? <span className="text-muted-foreground/70"> · {turn.activity}</span> : null}
            </span>
          </div>
        ) : editing !== null ? (
          <>
            <DraftEditor
              initial={editing.values}
              mode={review.mode}
              saving={saveDraft.isPending}
              onSave={save}
              onCancel={() => {
                saveDraft.reset();
                setEditing(null);
              }}
            />
            {saveDraft.isError && getInterviewErrorCode(saveDraft.error) !== "DRAFT_STALE" && (
              <FormulatorNotice
                message={{ text: errorText(saveDraft.error, "Your edit was not saved."), setup: false }}
                testId="formulator-draft-save-error"
              />
            )}
          </>
        ) : (
          body
        )}

        {destination !== "" && (
          <FieldRow label="Area · iteration" testId="formulator-destination">
            <span className="text-muted-foreground">{destination}</span>
          </FieldRow>
        )}
      </div>

      <aside className="flex w-[330px] shrink-0 flex-col gap-3.5">
        {showRedraft && (
          <RedraftBox
            instruction={instruction}
            onInstructionChange={setInstruction}
            running={redraftRunning}
            activity={turn.activity}
            failure={redraftFailure}
            disabled={submit.isPending || reviewQuery.isFetching}
            onRedraft={redraft}
          />
        )}
        <PartsTouched review={review} />
        <div className="glass-card flex flex-col gap-2 rounded-[10px] px-[18px] py-4">
          <h2 className="font-display text-[13px] font-semibold text-[#E4E4E7]">What happens next</h2>
          <p className="leading-relaxed text-muted-foreground" data-testid="formulator-next">
            {nextStepText(isCreate ? "create" : "improve", review.container, authorManagesAgenda)}
          </p>
        </div>

        {submit.isError && submitCode !== "BELOW_GROOMING_BAR" && (
          <FormulatorNotice
            message={{
              text:
                submitCode === "WORK_ITEM_CHANGED"
                  ? WORK_ITEM_CHANGED_MESSAGE
                  : submitCode === "DIFF_NOT_APPROVED"
                    ? "The draft changed since you opened it, so nothing was written. It has been reloaded; check it, then try again."
                    : errorText(submit.error, "Nothing was written to ADO."),
              setup: false,
            }}
            testId="formulator-submit-error"
          >
            {submitCode === "WORK_ITEM_CHANGED" && review.itemId && (
              <button
                type="button"
                className="font-medium text-link hover:underline"
                onClick={() => {
                  if (review.itemId) onRestartImprove(review.itemId);
                }}
              >
                Start again from the current item
              </button>
            )}
          </FormulatorNotice>
        )}

        <span className="flex-1" />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onKeepEditing}
            disabled={submit.isPending}
            className="inline-flex h-[30px] items-center rounded-lg border border-[#27272A] bg-[#1A1A1E] px-[11px] text-[13px] font-medium hover:bg-[#222227] disabled:opacity-40"
            data-testid="formulator-keep-editing"
          >
            Keep editing
          </button>
          <span className="flex-1" />
          <button
            type="button"
            onClick={write}
            disabled={busy || nothingToWrite || submitCode === "WORK_ITEM_CHANGED"}
            className={cn(
              "inline-flex h-[30px] items-center rounded-lg bg-primary px-[13px] text-[13px] font-semibold text-white transition-colors hover:bg-[#C91E38]",
              "disabled:pointer-events-none disabled:opacity-40"
            )}
            data-testid="formulator-submit"
          >
            {submit.isPending ? "Writing to ADO…" : isCreate ? "Create in ADO" : "Update in ADO"}
          </button>
        </div>
      </aside>
    </div>
  );
}
